/**
 * IntegrationDocs — a documentação de integração, dentro da plataforma
 * CI Sistemas de Informática Ltda. — FastALPR Parking (Onda 64)
 *
 * A documentação vivia numa página publicada fora do sistema, e abri-la exigia
 * autenticar num serviço de terceiros — barreira sem sentido para quem já tem
 * login aqui. Agora ela está atrás do mesmo login da plataforma.
 *
 * As DUAS verticais estão sempre disponíveis, independentemente da vertical
 * desta instalação: quem escreve o integrador atende clube e condomínio, e
 * precisa comparar os dois contratos lado a lado.
 *
 * O PDF sai por `window.print()` com uma folha de impressão própria. É de
 * propósito: o navegador gera PDF com texto selecionável, links funcionais e
 * tipografia de verdade. Uma biblioteca como html2canvas produziria uma imagem
 * — maior, sem busca por texto e ilegível quando ampliada.
 */
import React, { useMemo, useState, useCallback } from 'react';
import { FiPrinter, FiCopy, FiCheck, FiFileText } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import documentacao, { LISTA_VERTICAIS } from '../data/integrationDocs';
import '../styles/IntegrationDocs.css';

// `dangerouslySetInnerHTML` aqui é seguro e deliberado: o conteúdo é constante
// do próprio pacote (data/integrationDocs.js), não vem de rede nem do usuário.
// As marcações permitidas são só <strong>, <em> e <code>.
const html = txt => ({ __html: txt });

function Endpoint({ metodo, caminho, escopo }) {
  return (
    <div className="idoc-endpoint">
      <span className={`idoc-verbo idoc-verbo-${metodo.toLowerCase()}`}>{metodo}</span>
      <code className="idoc-caminho">{caminho}</code>
      {escopo && <span className="idoc-escopo">{escopo}</span>}
    </div>
  );
}

function Codigo({ txt, rotulo }) {
  const [copiado, setCopiado] = useState(false);
  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(txt);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1800);
    } catch (_) { /* sem permissão de área de transferência: o texto segue selecionável */ }
  };
  return (
    <div className="idoc-codigo-wrap">
      {rotulo && <span className="idoc-codigo-rotulo">{rotulo}</span>}
      <button className="idoc-copiar" onClick={copiar} title="Copiar" aria-label="Copiar o bloco de código">
        {copiado ? <FiCheck /> : <FiCopy />}
      </button>
      <pre className="idoc-codigo">{txt}</pre>
    </div>
  );
}

function Bloco({ b }) {
  switch (b.t) {
    case 'h2':
      return <h2 id={b.id} className="idoc-h2">{b.txt}</h2>;
    case 'h3':
      return <h3 className="idoc-h3" dangerouslySetInnerHTML={html(b.txt)} />;
    case 'p':
      return <p className="idoc-p" dangerouslySetInnerHTML={html(b.txt)} />;
    case 'lista':
      return (
        <ul className="idoc-lista">
          {b.itens.map((i, k) => <li key={k} dangerouslySetInnerHTML={html(i)} />)}
        </ul>
      );
    case 'passos':
      return (
        <ol className="idoc-passos">
          {b.itens.map((i, k) => <li key={k} dangerouslySetInnerHTML={html(i)} />)}
        </ol>
      );
    case 'tabela':
      return (
        <div className="idoc-tabela-wrap">
          <table className="idoc-tabela">
            <thead>
              <tr>{b.cab.map((c, k) => <th key={k} dangerouslySetInnerHTML={html(c)} />)}</tr>
            </thead>
            <tbody>
              {b.linhas.map((linha, k) => (
                <tr key={k}>{linha.map((celula, j) => <td key={j} dangerouslySetInnerHTML={html(String(celula))} />)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'codigo':
      return <Codigo txt={b.txt} rotulo={b.rotulo} />;
    case 'endpoint':
      return <Endpoint metodo={b.metodo} caminho={b.caminho} escopo={b.escopo} />;
    case 'nota':
      return <div className={`idoc-nota idoc-nota-${b.variante || 'info'}`} dangerouslySetInnerHTML={html(b.txt)} />;
    case 'parte':
      return (
        <section className="idoc-parte">
          <span className="idoc-parte-tag">{b.tag}</span>
          <h2 className="idoc-parte-titulo">{b.titulo}</h2>
          <p className="idoc-parte-dir" dangerouslySetInnerHTML={html(b.dir)} />
        </section>
      );
    default:
      return null;
  }
}

export default function IntegrationDocs() {
  const { can, vertical } = useAuth();
  // Abre na vertical da instalação, mas as duas ficam a um clique — quem
  // escreve o integrador atende as duas e precisa comparar os contratos.
  const [aba, setAba] = useState(
    LISTA_VERTICAIS.some(v => v.chave === vertical) ? vertical : LISTA_VERTICAIS[0].chave
  );

  const doc = useMemo(() => documentacao(aba), [aba]);
  const sumario = useMemo(
    () => doc.blocos.filter(b => b.t === 'h2' && b.id).map(b => ({ id: b.id, txt: b.txt })),
    [doc]
  );

  const imprimir = useCallback(() => window.print(), []);

  if (!can('integration_docs')) {
    return (
      <div className="idoc-page">
        <h1>Documentação da Integração</h1>
        <p>Acesso restrito.</p>
      </div>
    );
  }

  return (
    <div className="idoc-page">
      <div className="idoc-cabecalho">
        <div>
          <h1 className="idoc-titulo">{doc.vertical.titulo}</h1>
          <p className="idoc-lede">
            Contrato de integração entre o FastALPR Parking e o sistema de gestão.
            Carga do cadastro, visitantes com autorização por visita, e o webhook de placa reconhecida.
          </p>
        </div>
        <button className="idoc-imprimir" onClick={imprimir}>
          <FiPrinter /> Baixar em PDF
        </button>
      </div>

      {/* Seletor da vertical: as duas estão sempre disponíveis. */}
      <div className="idoc-abas" role="tablist">
        {LISTA_VERTICAIS.map(v => (
          <button
            key={v.chave}
            role="tab"
            aria-selected={aba === v.chave}
            className={`idoc-aba ${aba === v.chave ? 'ativa' : ''}`}
            onClick={() => setAba(v.chave)}
          >
            <FiFileText /> {v.nome}
            {v.chave === vertical && <span className="idoc-aba-marca">esta instalação</span>}
          </button>
        ))}
      </div>

      <p className="idoc-dica-print">
        O botão <strong>Baixar em PDF</strong> abre a janela de impressão do navegador: escolha
        <em> Salvar como PDF</em> em “Destino”. Sai só o documento, sem menu nem botões, com os
        links preservados. O PDF traz a vertical selecionada acima.
      </p>

      <nav className="idoc-sumario">
        <p className="idoc-sumario-titulo">Conteúdo</p>
        <ol>
          {sumario.map((s, k) => (
            <li key={s.id}>
              <a href={`#${s.id}`}>
                <span className="idoc-sumario-num">{String(k + 1).padStart(2, '0')}</span>
                {s.txt}
              </a>
            </li>
          ))}
        </ol>
      </nav>

      <article className="idoc-corpo">
        {doc.blocos.map((b, k) => <Bloco key={k} b={b} />)}
      </article>

      <footer className="idoc-rodape">
        <p>CI Sistemas de Informática Ltda. · FastALPR Parking · vertical {doc.vertical.nome}</p>
        <p>Contratos extraídos do código em produção. Revisão de 09/09/2026.</p>
      </footer>
    </div>
  );
}
